import React from "react";
import { useDropzone } from "react-dropzone";
import { Loader2, CheckCircle2, AlertCircle, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useApplicationExtraction } from "@/hooks/useApplicationExtraction";
import { ExtractedApplicationFields } from "@/types/extraction";

interface ApplicationPdfUploadProps {
  onFieldsExtracted: (fields: ExtractedApplicationFields) => void;
}

const ApplicationPdfUpload: React.FC<ApplicationPdfUploadProps> = ({ onFieldsExtracted }) => {
  const { status, fileName, errorMessage, showSlowMessage, handleDrop, reset } =
    useApplicationExtraction(onFieldsExtracted);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    maxFiles: 1,
    disabled: status === "extracting",
  });

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Auto-fill from Application PDF</h3>

        {status === "idle" && (
          <div
            {...getRootProps()}
            className={cn(
              "flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors",
              isDragActive
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
            )}
          >
            <input {...getInputProps()} />
            <Upload className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                {isDragActive ? "Drop PDF here" : "Drop MCA application PDF to auto-fill"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">or click to browse</p>
            </div>
          </div>
        )}

        {status === "extracting" && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div>
              <p className="text-sm font-medium">Extracting business information...</p>
              <p className="text-sm font-medium">You can upload bank statements while you wait!</p>
              <p className="mt-1 text-xs text-muted-foreground">{fileName}</p>
              {showSlowMessage && (
                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  Taking longer than expected...
                </p>
              )}
            </div>
          </div>
        )}

        {status === "done" && (
          <div className="flex items-center justify-between rounded-lg border border-green-600/20 bg-green-50 px-4 py-3 dark:bg-green-950">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                  Form auto-filled
                </p>
                <p className="text-xs text-green-700 dark:text-green-300">{fileName}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={reset} className="text-xs">
              Upload different
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-4 text-center">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm font-medium text-destructive">
                {errorMessage || "Extraction failed"}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">You can fill in the form manually</p>
            <Button variant="outline" size="sm" onClick={reset}>
              Try again
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApplicationPdfUpload;
